import { useEffect, useState } from 'react';
import { useClient } from 'jsonapi-react';
import { Label } from '@/components/ui/label';

const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';

const ETAPAS = [
  { value: 'serie_3_em', label: '3ª EM' },
  { value: 'ano_9_ef', label: '9º EF' },
  { value: 'ano_5_ef', label: '5º EF' },
  { value: 'ano_2_ef', label: '2º EF' }
];

const TIPOS_CADERNO = [
  { value: 'exam', label: 'Provas' },
  { value: 'train', label: 'Simulados' },
  { value: 'open', label: 'Exercícios' },
  { value: 'desafio', label: 'Desafio ao vivo' }
];

const DISPONIBILIDADE = [
  { value: undefined, label: 'Todas' },
  { value: 'true', label: 'Inéditas' },
  { value: 'false', label: 'Já usadas' }
];

/**
 * Form reativo (sem submit) com 4-5 dimensões.
 * Parent escuta onChange e re-fetcha o count.
 */
export default function QuestionsFilters({ filters, onChange, taxonomies = [] }) {
  const client = useClient();
  const [descritores, setDescritores] = useState([]);

  useEffect(() => {
    const materiaIds = filters['taxonomies.id'] || [];
    if (materiaIds.length === 0) {
      setDescritores([]);
      return;
    }
    Promise.all(
      materiaIds.map(id =>
        client
          .fetch(`taxonomies/used_by_questions?parent_id=${id}`)
          .then(r => r?.data || [])
          .catch(() => [])
      )
    ).then(results => setDescritores(results.flat()));
  }, [filters['taxonomies.id']]); // eslint-disable-line react-hooks/exhaustive-deps

  const setFilter = (key, value) => {
    const next = { ...filters };
    if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
      delete next[key];
    } else {
      next[key] = value;
    }
    onChange(next);
  };

  const toggleArrayValue = (key, value) => {
    const current = Array.isArray(filters[key]) ? filters[key] : [];
    const next = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
    if (key === 'taxonomies.id' && next.length === 0) {
      const cleared = { ...filters };
      delete cleared['taxonomies.id'];
      delete cleared.descritor_ids;
      onChange(cleared);
    } else {
      setFilter(key, next);
    }
  };

  return (
    <div className="grid gap-x-8 gap-y-5 md:grid-cols-2">
      <FilterGroup label="Disponibilidade">
        {DISPONIBILIDADE.map(opt => (
          <Chip
            key={String(opt.value)}
            active={filters.ineditas === opt.value}
            onClick={() => setFilter('ineditas', opt.value)}
          >
            {opt.label}
          </Chip>
        ))}
      </FilterGroup>

      <FilterGroup label="Etapa">
        {ETAPAS.map(e => (
          <Chip
            key={e.value}
            active={(filters.etapa || []).includes(e.value)}
            onClick={() => toggleArrayValue('etapa', e.value)}
          >
            {e.label}
          </Chip>
        ))}
      </FilterGroup>

      <FilterGroup label="Usadas em (tipo de caderno)">
        {TIPOS_CADERNO.map(t => (
          <Chip
            key={t.value}
            active={(filters.usadas_em || []).includes(t.value)}
            onClick={() => toggleArrayValue('usadas_em', t.value)}
          >
            {t.label}
          </Chip>
        ))}
      </FilterGroup>

      <FilterGroup label="Matéria">
        {taxonomies.map(tax => (
          <Chip
            key={tax.id}
            active={(filters['taxonomies.id'] || []).includes(tax.id)}
            onClick={() => toggleArrayValue('taxonomies.id', tax.id)}
          >
            {tax.name}
          </Chip>
        ))}
      </FilterGroup>

      {descritores.length > 0 && (
        <div className="md:col-span-2">
          <FilterGroup label="Descritor">
            {descritores.map(d => (
              <Chip
                key={d.id}
                active={(filters.descritor_ids || []).includes(d.id)}
                onClick={() => toggleArrayValue('descritor_ids', d.id)}
              >
                {d.name}
              </Chip>
            ))}
          </FilterGroup>
        </div>
      )}
    </div>
  );
}

function FilterGroup({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wide text-neutral-light">{label}</Label>
      <div className="mt-2.5 flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
}

/**
 * Filter chip com micro-feedback no press + smooth transition de cor.
 * Tailwind hover: já respeita @media (hover) — não dispara em touch tap.
 */
function Chip({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-active={active}
      className={[
        'inline-flex select-none items-center rounded-full border px-3 py-1 text-xs',
        'transition-[transform,colors,border-color,background-color] duration-200',
        'active:scale-[0.96]',
        active ? 'border-primary bg-primary text-white shadow-[0_1px_2px_rgba(0,0,0,0.06)]' : 'hover:border-primary/40 hover:bg-primary/[0.03] border-neutral-lightest bg-white text-neutral-dark'
      ].join(' ')}
      style={{ transitionTimingFunction: EASE_OUT }}
    >
      {children}
    </button>
  );
}
